import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cv_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('cv_token');
      localStorage.removeItem('cv_user');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  login2FA: (data) => api.post('/auth/login/2fa', data),
  getMe: () => api.get('/auth/me'),
  setup2FA: () => api.post('/auth/2fa/setup'),
  verify2FA: (data) => api.post('/auth/2fa/verify', data),
  disable2FA: (data) => api.post('/auth/2fa/disable', data),
  get2FAStatus: () => api.get('/auth/2fa/status'),
};

// Wallet
export const walletAPI = {
  getWallet: () => api.get('/wallet'),
  simulateDeposit: (data) => api.post('/wallet/deposit-sim', data),
  requestWithdraw: (data) => api.post('/wallet/withdraw', data),
  getWithdrawalEligibility: () => api.get('/wallet/withdrawal-eligibility'),
};

// Deposits (On-chain)
export const depositAPI = {
  getPlatformAddress: () => api.get('/deposits/platform-address'),
  submit: (data) => api.post('/deposits/submit', data),
  getMyDeposits: (params) => api.get('/deposits/my', { params }),
  getDetail: (id) => api.get(`/deposits/${id}`),
  recheck: (id) => api.post(`/deposits/${id}/recheck`),
  submitSupport: (data) => api.post('/deposits/support', data),
  getMyTickets: () => api.get('/deposits/support/my'),
  getSupportedCoins: () => api.get('/deposits/supported-coins'),
  createSession: (data) => api.post('/deposits/session', data),
  getSession: (id) => api.get(`/deposits/session/${id}`),
  cancelSession: (id) => api.post(`/deposits/session/${id}/cancel`),
  confirmTransfer: (id) => api.post(`/deposits/session/${id}/confirm-transfer`),
};

// Exchange
export const exchangeAPI = {
  getRates: () => api.get('/exchange/rates'),
  trade: (data) => api.post('/exchange/trade', data),
};

// Rate Protection
export const rateProtectionAPI = {
  getStatus: () => api.get('/rate-protection/status'),
};

// Transactions
export const transactionAPI = {
  getTransactions: (params) => api.get('/transactions', { params }),
};

// KYC
export const kycAPI = {
  getStatus: () => api.get('/kyc/status'),
  submit: (data) => api.post('/kyc/submit', data),
};

// Referral
export const referralAPI = {
  getInfo: () => api.get('/referral'),
};

// Prices
export const priceAPI = {
  getPrices: () => api.get('/prices'),
};

// Market Overview
export const marketAPI = {
  getOverview: () => api.get('/market/overview'),
};

// Platform config
export const platformAPI = {
  getConfig: () => api.get('/platform/config'),
};

// Admin
export const adminAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserDetail: (userId) => api.get(`/admin/user/${userId}`),
  updateUserBalance: (userId, data) => api.post(`/admin/user/${userId}/balance`, data),
  getKYCQueue: (params) => api.get('/admin/kyc-queue', { params }),
  kycAction: (kycId, data) => api.post(`/admin/kyc/${kycId}/action`, data),
  getWithdrawals: (params) => api.get('/admin/withdrawals', { params }),
  withdrawAction: (id, data) => api.post(`/admin/withdrawals/${id}/action`, data),
  getStats: () => api.get('/admin/stats'),
  createAdmin: (data) => api.post('/admin/create', data),
  getEscalations: (params) => api.get('/admin/escalations', { params }),
  resolveEscalation: (id) => api.post(`/admin/escalations/${id}/resolve`),
  getPaymentLogs: (params) => api.get('/admin/payment-logs', { params }),
  getActivityFeed: (params) => api.get('/admin/activity-feed', { params }),
  getPlatformSettings: () => api.get('/admin/platform-settings'),
  updatePlatformSettings: (data) => api.put('/admin/platform-settings', data),
  getCardApplications: (params) => api.get('/admin/card-applications', { params }),
  cardAppAction: (id, action) => api.post(`/admin/card-applications/${id}/action?action=${action}`),
  getDeposits: (params) => api.get('/admin/deposits', { params }),
  depositAction: (id, data) => api.post(`/admin/deposits/${id}/action`, data),
  triggerDepositCheck: () => api.post('/admin/deposits/check-now'),
  getSupportTickets: (params) => api.get('/admin/deposit-support', { params }),
  respondSupportTicket: (id, data) => api.post(`/admin/deposit-support/${id}/respond`, data),
  // SafeCoin Admin
  getSafecoinStats: () => api.get('/admin/safecoin/stats'),
  grantSafecoin: (data) => api.post('/admin/safecoin/grant', data),
  revokeSafecoin: (data) => api.post('/admin/safecoin/revoke', data),
  getSafecoinLeaderboard: () => api.get('/admin/safecoin/leaderboard'),
  distributeLeaderboardBonuses: () => api.post('/admin/safecoin/distribute-leaderboard'),
  // Coin Management
  getSupportedCoins: () => api.get('/admin/supported-coins'),
  updateSupportedCoins: (data) => api.put('/admin/supported-coins', data),
  // GameFuel Admin
  getGamefuelConfig: () => api.get('/admin/gamefuel/config'),
  updateGamefuelConfig: (data) => api.put('/admin/gamefuel/config', data),
  getGamefuelStats: () => api.get('/admin/gamefuel/stats'),
};

// Support Chat
export const chatAPI = {
  sendMessage: (data) => api.post('/support/chat', data),
  getHistory: (sessionId) => api.get('/support/chat/history', { params: { session_id: sessionId } }),
  escalate: (data) => api.post('/support/escalate', data),
};

// SafeCoin
export const safecoinAPI = {
  getBalance: () => api.get('/safecoin/balance'),
  getHistory: (params) => api.get('/safecoin/history', { params }),
  getMissions: () => api.get('/safecoin/missions'),
  getLeaderboard: () => api.get('/safecoin/leaderboard'),
  getSpendOptions: () => api.get('/safecoin/spend-options'),
  spend: (data) => api.post('/safecoin/spend', data),
};

// GameFuel
export const gamefuelAPI = {
  getStatus: () => api.get('/gamefuel/status'),
  getHistory: (params) => api.get('/gamefuel/history', { params }),
  checkCredit: (feeAmount) => api.get('/gamefuel/check-credit', { params: { fee_amount: feeAmount } }),
  cardPurchase: (data) => api.post('/card-purchase', data),
};

// Web3 Wallet Connect
export const web3API = {
  getChallenge: (data) => api.post('/wallet/challenge', data),
  verifyWallet: (data) => api.post('/wallet/verify', data),
  linkWallet: (data) => api.post('/wallet/link', data),
  getConnectedWallets: () => api.get('/wallet/connected'),
  unlinkWallet: (chain) => api.delete(`/wallet/unlink/${chain}`),
  walletLogin: (data) => api.post('/auth/wallet-login', data),
};

export default api;
